const R = 287.05 // Specific gas constant for dry air in J/(kg·K)
const EARTH_RADIUS = 6371220 // Radius of earth in meters
const G = 9.81 // Acceleration due to gravity in m/s²
const radToDeg = rad => rad * (180 / Math.PI)
const clamp = (value, min, max) => Math.max(min, Math.min(max, value))
const multiplyMatrices = (a, b) => a.map((row, i) => row.map((_, j) => row.reduce((sum, _, k) => sum + a[i][k] * b[k][j], 0)));
const multiplyMatrixByScalar = (matrix, scalar) => matrix.map(row => row.map(value => value * scalar));
const addMatrices = (a, b) => a.map((row, i) => row.map((value, j) => value + b[i][j]));


export default class SixDofSim {
  constructor({
    mass,
    Ix,
    Iy,
    Ma,
    Cd0,
    Cda2,
    Cla,
    Clp,
    Cma0,
    Cma2,
    CmqCma0,
    Cmpa0,
    Cmpa2,
    Cnq,
    airPressure,
    diameter,
    temperature,
    windSpeed,
    windDirection,
    calibersPerTurn,
    v0,
    phi0,
    theta0,
    pitchRate0,
    yawRate0,
    alpha0,
    beta0
  }) {
    this.mass = mass
    this.Ix = Ix
    this.Iy = Iy
    this.Iz = Iy
    this.Ma = Ma
    this.Cd0 = Cd0
    this.Cda2 = Cda2
    this.Clp = Clp
    this.Cla = Cla
    this.Cma0 = Cma0
    this.Cma2 = Cma2
    this.CmqCma0 = CmqCma0
    this.Cmpa0 = Cmpa0
    this.Cmpa2 = Cmpa2
    this.Cnq = Cnq
    this.diameter = diameter
    this.area = (Math.PI * (diameter / 2) ** 2) // m²
    this.airPressure = airPressure
    this.temperature = temperature // K
    this.rho = this.airPressure / (R * this.temperature)
    this.windSpeed = windSpeed
    this.windDirection = windDirection
    this.windX = windSpeed * Math.cos(windDirection + Math.PI)
    this.windY = 0
    this.windZ = windSpeed * Math.sin(windDirection + Math.PI)
    console.log(this.windX, this.windY, this.windZ)
    this.lastTimeLogged = 0
    this.states = []
    const spinRate = 2 * Math.PI * v0 / (diameter * calibersPerTurn)
    const Q = Math.sin((theta0 + beta0)) ** 2 + Math.cos((theta0 + beta0)) ** 2 * Math.cos((phi0 + alpha0)) ** 2
    const unitXRate = (-yawRate0 * Math.cos((theta0 + beta0)) ** 2 * Math.sin((phi0 + alpha0)) * Math.cos((phi0 + alpha0)) + pitchRate0 * Math.sin((theta0 + beta0))) / Math.sqrt(Q)
    const unitYRate = (yawRate0 * Math.cos((theta0 + beta0)) ** 2 * Math.cos((phi0 + alpha0)) ** 2 + yawRate0 * Math.sin((theta0 + beta0)) ** 2) / Math.sqrt(Q)
    const unitZRate = (-yawRate0 * Math.sin((theta0 + beta0)) * Math.cos((theta0 + beta0)) * Math.sin((phi0 + alpha0)) - pitchRate0 * Math.cos((theta0 + beta0)) * Math.cos((phi0 + alpha0))) / Math.sqrt(Q)
    const unitX = Math.cos((phi0 + alpha0)) * Math.cos((theta0 + beta0))
    const unitY = Math.sin((phi0 + alpha0)) * Math.cos((theta0 + beta0))
    const unitZ = Math.sin((theta0 + beta0))
    const vx = v0 * Math.cos((phi0)) * Math.cos((theta0))
    const vy = v0 * Math.sin((phi0)) * Math.cos((theta0))
    const vz = v0 * Math.sin((theta0))
    const rollRate  = ((Ix * spinRate) / Iy) * unitX   + unitY * unitZRate   - unitZ * unitYRate
    const pitchRate = ((Ix * spinRate) / Iy) * unitY   - unitX * unitZRate   + unitZ * unitXRate
    const yawRate   = ((Ix * spinRate) / Iy) * unitZ   + unitX * unitYRate   - unitY * unitXRate
    const posX = 0
    const posY = 0
    const posZ = 0
    console.log('initial state', {
      vx, vy, vz,
      rollRate, pitchRate, yawRate,
      unitX, unitY, unitZ,
      posX, posY, posZ
    })
    console.log(`v(${vx.toFixed(1)}, ${vy.toFixed(1)}, ${vz.toFixed(1)})`)
    console.log(`u(${unitX.toFixed(6)}, ${unitY.toFixed(6)}, ${unitZ.toFixed(6)})`)
    console.log(`h(${rollRate.toFixed(6)}, ${pitchRate.toFixed(6)}, ${yawRate.toFixed(6)}`)
    this.state = [
      vx, vy, vz,
      rollRate, pitchRate, yawRate,
      unitX, unitY, unitZ,
      posX, posY, posZ
    ]
  }

  interpolate (mach, coeffs) {
    for (let i = 0; i < coeffs.length - 1; ++i) {
      const ma1 = coeffs[i][0]
      const ma2 = coeffs[i + 1][0]
      if (mach >= ma1 && mach <= ma2) {
        const cv1 = coeffs[i][1]
        const cv2 = coeffs[i + 1][1]
        const v = cv1 + ((cv2 - cv1) * (mach - ma1)) / (ma2 - ma1)
        return v
      }
    }
    return coeffs[coeffs.length - 1][1]
  }

  mach (v) {
    const gamma = 1.4
    const a = Math.sqrt(gamma * R * this.temperature)
    const machNumber = v / a
    return machNumber
  }

  getSg (spinRate, Cma, v) {
    const { Ix, Iy, rho, diameter } = this
    const numerator = 2 * Math.pow(Ix, 2) * Math.pow(spinRate, 2);
    const denominator = Math.PI * rho * Iy * Cma * Math.pow(diameter, 3) * Math.pow(v, 2);
    const Sg = numerator / denominator;
    return Sg;
  }

  getSd (Cla, Cd, Cnpa, Cma, Cmq) {
    const { Ix, Iy, diameter, mass } = this
    const k1 = (mass * diameter ** 2) / (2 * Ix)
    const k2 = (mass * diameter ** 2) / (2 * Iy)
    const Sd = (2 * (Cla + k1 * Cnpa)) / (Cla - Cd - k2 * (Cmq + Cma))
    return Sd
  }

  getAlphaBeta (v, u) {
    const vMag = Math.sqrt(v[0]**2 + v[1]**2 + v[2]**2);
    const vNorm = [v[0] / vMag, v[1] / vMag, v[2] / vMag];

    const uMag = Math.sqrt(u[0]**2 + u[1]**2 + u[2]**2);
    const uNorm = [u[0] / uMag, u[1] / uMag, u[2] / uMag];

    const cross = [
      vNorm[1] * uNorm[2] - vNorm[2] * uNorm[1],
      vNorm[2] * uNorm[0] - vNorm[0] * uNorm[2],
      vNorm[0] * uNorm[1] - vNorm[1] * uNorm[0]
    ];

    const dot = vNorm[0] * uNorm[0] + vNorm[1] * uNorm[1] + vNorm[2] * uNorm[2];

    // Calculate the skew-symmetric matrix of the cross product
    const crossMatrix = [
      [0, -cross[2], cross[1]],
      [cross[2], 0, -cross[0]],
      [-cross[1], cross[0], 0]
    ];

    const identity = [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1]
    ];

    const crossNorm = Math.sqrt(cross[0]**2 + cross[1]**2 + cross[2]**2);
    const crossMatrixSquared = multiplyMatrices(crossMatrix, crossMatrix);
    const factor = (1 - dot) / (crossNorm**2);

    const rotationMatrix = addMatrices(
      addMatrices(identity, crossMatrix),
      multiplyMatrixByScalar(crossMatrixSquared, factor)
    );

    // Extract Euler angles from the rotation matrix
    const pitch = Math.atan2(rotationMatrix[1][0], rotationMatrix[0][0]);
    const yaw = Math.asin(rotationMatrix[2][0]);

    return [
      pitch,
      yaw
    ]
  }

  equationsOfMotion (time, state, dt) {
    let [ Vx, Vy, Vz, rollRate, pitchRate, yawRate, unitX, unitY, unitZ, posX, posY ] = state
    const vx = Vx - this.windX
    const vy = Vy - this.windY
    const vz = Vz - this.windZ
    const { diameter, mass, rho, area, Iy } = this
    const v = Math.sqrt(vx ** 2 + vy ** 2 + vz ** 2)
    const alphaT = Math.acos(clamp((vx * unitX + vy * unitY + vz * unitZ) / v, -1, 1))
    const mach = this.mach(v)
    const hdotx = rollRate * unitX + pitchRate * unitY + yawRate * unitZ
    const xdotx = unitX * unitX + unitY * unitY + unitZ * unitZ
    if (Math.abs(1 - xdotx) > .00001) {
      console.log('unit vector not normalized, dt too large', { unitX, unitY, unitZ })
    }
    const spinRate = (this.Iy / this.Ix) * hdotx

    const cd0 = this.interpolate(mach, this.Cd0)
    const cdα2 = this.interpolate(mach, this.Cda2)
    const clα = this.interpolate(mach, this.Cla)
    const clp = this.interpolate(mach, this.Clp)
    const cmα0 = this.interpolate(mach, this.Cma0)
    const cmα2 = this.interpolate(mach, this.Cma2)
    const cmq = this.interpolate(mach, this.CmqCma0)
    const cmpα0 = this.interpolate(mach, this.Cmpa0)
    const cmpα2 = this.interpolate(mach, this.Cmpa2)

    const cd = cd0 + cdα2 * (Math.sin(alphaT) ** 2)
    const cmα = cmα0 + cmα2 * (Math.sin(alphaT) ** 2)
    const cmpα = cmpα0 + cmpα2 * (Math.sin(alphaT) ** 2)

    const Ĉlα  = (clα  * rho *     area                           ) / (2 * mass)
    const Ĉd   = (cd   * rho * v * area                           ) / (2 * mass)
    const Ĉmα  = (cmα  * rho * v * area * diameter                ) / (2 * Iy)
    const Ĉmq  = (cmq  * rho * v * area * diameter ** 2           ) / (2 * Iy)
    const Ĉlp  = (clp  * rho * v * area * diameter ** 2 * spinRate) / (2 * Iy)
    const Ĉmpα = (cmpα * rho *     area * diameter ** 2 * spinRate) / (2 * Iy)

    const gx = -G * posX / EARTH_RADIUS
    const gy = -G * (1 - 2 * posY / EARTH_RADIUS)
    const gz = 0

    this.coeffs = { cd, clα, clp, cmα, cmq, cmpα }
    const Sg = this.getSg(spinRate, cmα, v)
    const Sd = this.getSd(Ĉlα, cd, cmpα, cmα, cmq)
    const minSg = 1 / (Sd * (2 - Sd))
    const stable = Sd > 0 && Sd < 2 && Sg >= minSg && Sg > 1
    this.Sg = Sg
    this.Sd = Sd
    this.stable = stable

    return [
      -Ĉd * vx + Ĉlα * (v ** 2 * unitX - v * vx * Math.cos(alphaT)) + gx,
      -Ĉd * vy + Ĉlα * (v ** 2 * unitY - v * vy * Math.cos(alphaT)) + gy,
      -Ĉd * vz + Ĉlα * (v ** 2 * unitZ - v * vz * Math.cos(alphaT)) + gz,
      Ĉlp * unitX + Ĉmα * (vy * unitZ - vz * unitY) + Ĉmpα * (vx - v * unitX * Math.cos(alphaT)) + Ĉmq * (rollRate - hdotx * unitX),
      Ĉlp * unitY + Ĉmα * (vz * unitX - vx * unitZ) + Ĉmpα * (vy - v * unitY * Math.cos(alphaT)) + Ĉmq * (pitchRate - hdotx * unitY),
      Ĉlp * unitZ + Ĉmα * (vx * unitY - vy * unitX) + Ĉmpα * (vz - v * unitZ * Math.cos(alphaT)) + Ĉmq * (yawRate - hdotx * unitZ),
      pitchRate * unitZ - yawRate * unitY,
      yawRate * unitX - rollRate * unitZ,
      rollRate * unitY - pitchRate * unitX,
      Vx,
      Vy + (Vx ** 2) / (2 * EARTH_RADIUS),
      Vz
    ]
  }

  // Runge-Kutta 4th order method
  rk4(t, dt) {
    let k1 = this.equationsOfMotion(t, this.state)
    let k2 = this.equationsOfMotion(t, this.state.map((v, i) => v + k1[i] * dt / 2))
    let k3 = this.equationsOfMotion(t, this.state.map((v, i) => v + k2[i] * dt / 2))
    let k4 = this.equationsOfMotion(t, this.state.map((v, i) => v + k3[i] * dt))

    let y = [ ...this.state ]
    let deltas = []
    for (let i = 0; i < y.length; i++) {
      const delta = (dt / 6) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i])
      deltas.push(delta)
    }
    for (let i = 0; i < y.length; i++) {
      y[i] += deltas[i]
    }
    return y
  }

  integrate ({ maxTime, dt, resultInterval }) {
    let time = 0
    let results = []
    while (time <= maxTime) {
      this.state = this.rk4(time, dt)
      if (time - this.lastTimeLogged > resultInterval) {
        this.lastTimeLogged = time
        const [ vx, vy, vz, rollRate, pitchRate, yawRate, unitX, unitY, unitZ, posX, posY, posZ ] = this.state
        const v = Math.sqrt(vx ** 2 + vy ** 2 + vz ** 2)
        const [ alpha, beta ] = this.getAlphaBeta([vx, vy, vz], [unitX, unitY, unitZ])
        const alphaT = Math.acos(clamp((vx * unitX + vy * unitY + vz * unitZ) / v, -1, 1))
        //const alphaT = Math.sqrt(alpha ** 2 + beta ** 2)
        const mach = this.mach(v)
        const hdotx = rollRate * unitX + pitchRate * unitY + yawRate * unitZ
        const spinRate = (this.Iy / this.Ix) * hdotx
        if (isNaN(this.state[0]) || isNaN(this.state[1]) || isNaN(this.state[2])) {
          console.log('NaN detected')
          break
        }
        results.push({
          vx, vy, vz,
          rollRate, pitchRate, yawRate,
          unitX, unitY, unitZ,
          posX, posY, posZ,
          alpha, beta, alphaT,
          alphaDeg: radToDeg(alpha), betaDeg: radToDeg(beta), alphaTDeg: radToDeg(alphaT),
          mach, v,
          spinRate,
          Sg: this.Sg,
          Sd: this.Sd,
          stable: this.stable ? 1 : 0,
          ...this.coeffs,
          ...this.forces
        })
      }

      time += dt
    }
    return results
  }

}
